import React, { useEffect, useMemo, useState } from 'react';
import { simboloMoneda } from '../services/negocioActual';
import { obtenerRenglonesVenta, editarVenta, obtenerEnvioVenta } from '../services/balanceService';
import { obtenerClientes, crearCliente } from '../services/clientesService';
import { obtenerCuentas, iconoTipo } from '../services/cuentasService';
import { CANALES } from '../utils/canales';
import { fechaLocal, horaLocal, timestampParaFecha } from '../utils/fecha';
import { mensajeAmigable } from '../utils/errores';
import { numeroSeguro } from '../utils/validacion';
import CampoNumero from './CampoNumero';
import { useToast } from '../context/ToastContext';
import { obtenerFotoPrincipal } from '../utils/categorias';
import { optimizarFoto } from '../utils/imagen';
import { esPorPeso, formatoCantidad, redondearCantidad, unidadDe } from '../utils/unidades';

const fmt = (n) => `${simboloMoneda()} ${Number(n || 0).toLocaleString('es-HN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// Editar una venta ya registrada. Al guardar, la base revierte la venta
// original y crea la corregida en una sola operación (atómica).
export default function EditarVentaModal({ referencia, productos, onCerrar, onGuardado }) {
  const { mostrarToast } = useToast();
  const [items, setItems] = useState(null);
  const [clientes, setClientes] = useState([]);
  const [cuentas, setCuentas] = useState([]);
  const [clienteId, setClienteId] = useState('');
  const [cuentaId, setCuentaId] = useState('');
  const [descuento, setDescuento] = useState('');
  const [envio, setEnvio] = useState('');
  const [fechaVenta, setFechaVenta] = useState(fechaLocal());
  const [concepto, setConcepto] = useState('');
  const [nota, setNota] = useState('');
  const [canal, setCanal] = useState('');
  const [aCredito, setAcredito] = useState(false); // pagada vs a crédito (fiado)
  const [pagaCon, setPagaCon] = useState(''); // efectivo: para el vuelto
  const [nuevoCli, setNuevoCli] = useState(false);
  const [cliNombre, setCliNombre] = useState('');
  const [cliTel, setCliTel] = useState('');
  const [guardandoCli, setGuardandoCli] = useState(false);
  const [error, setError] = useState(null);
  const [guardando, setGuardando] = useState(false);

  useEffect(() => {
    let activo = true;
    Promise.all([obtenerRenglonesVenta(referencia), obtenerClientes(), obtenerCuentas(), obtenerEnvioVenta(referencia).catch(() => 0)])
      .then(([renglones, cli, ctas, envioActual]) => {
        if (!activo) return;
        if (!renglones.length) { setError('No se encontró la venta.'); return; }
        setClientes(cli);
        setCuentas(ctas);
        setClienteId(renglones[0].cliente_id || '');
        setCuentaId(renglones[0].cuenta_id || (ctas[0]?.id ?? ''));
        setConcepto(renglones[0].concepto || '');
        setNota(renglones[0].nota || '');
        setCanal(renglones[0].canal || '');
        if (envioActual > 0) setEnvio(String(envioActual));
        // Precargar la fecha original de la venta (si existe).
        if (renglones[0].vendido_en) setFechaVenta(fechaLocal(new Date(renglones[0].vendido_en)));

        const its = renglones.map((r) => {
          const prod = productos.find((p) => p.id === r.producto_id);
          const precio = prod ? Number(prod.precio_venta || 0) : Number(r.precio_unitario || 0);
          const cantidad = Number(r.cantidad || 1);
          const stockMax = redondearCantidad(Number(prod ? (prod.stock || 0) : 0) + cantidad); // stock que quedará disponible al revertir
          return {
            producto_id: r.producto_id, codigo: r.codigo_producto, nombre: r.nombre_producto, precio, cantidad, stockMax,
            cantTexto: String(cantidad), unidad: unidadDe(prod), foto: prod ? obtenerFotoPrincipal(prod) : null,
          };
        });
        setItems(its);

        // Reconstruir el descuento original (precio de lista − total cobrado).
        const listaTotal = its.reduce((s, i) => s + i.precio * i.cantidad, 0);
        const cobrado = renglones.reduce((s, r) => s + Number(r.total || 0), 0);
        const desc = Math.round(listaTotal - cobrado);
        if (desc > 0) setDescuento(String(desc));
      })
      .catch((e) => activo && setError(mensajeAmigable(e)));
    return () => { activo = false; };
  }, [referencia, productos]);

  const cambiar = (id, delta) => {
    setItems((prev) => prev.map((i) => {
      if (i.producto_id !== id) return i;
      const nueva = redondearCantidad(i.cantidad + delta);
      if (nueva <= 0 || (!esPorPeso(i.unidad) && nueva < 1)) return i;
      if (nueva > i.stockMax) { mostrarToast(`Máximo ${formatoCantidad(i.stockMax, i.unidad)} disponibles`, 'error'); return i; }
      return { ...i, cantidad: nueva, cantTexto: String(nueva) };
    }));
  };
  // Productos por peso: las libras se escriben (ej. 3.25).
  const escribirCantidad = (id, texto) => setItems((prev) => prev.map((i) => (i.producto_id === id
    ? { ...i, cantTexto: texto, cantidad: redondearCantidad(numeroSeguro(texto)) } : i)));
  const quitar = (id) => setItems((prev) => prev.filter((i) => i.producto_id !== id));

  const subtotal = useMemo(() => (items || []).reduce((s, i) => s + i.precio * i.cantidad, 0), [items]);
  const desc = Math.min(numeroSeguro(descuento), subtotal);
  const totalProductos = subtotal - desc;
  // El envío entra a la cuenta (pagada) o se suma a lo que se debe (crédito).
  const envioNum = Math.max(numeroSeguro(envio), 0);
  const total = totalProductos + envioNum;

  // Vuelto: solo si la venta es pagada y entra a una cuenta de efectivo.
  const cuentaSel = cuentas.find((c) => c.id === cuentaId);
  const esEfectivo = cuentaSel?.tipo === 'efectivo';
  const pagaConNum = numeroSeguro(pagaCon);
  const vuelto = pagaConNum - total;

  // Crea un cliente al vuelo y lo deja seleccionado.
  const guardarClienteRapido = async () => {
    if (!cliNombre.trim()) { mostrarToast('Escribe el nombre del cliente', 'error'); return; }
    setGuardandoCli(true);
    try {
      const nuevo = await crearCliente({ nombre: cliNombre, telefono: cliTel });
      setClientes((prev) => [...prev, nuevo].sort((a, b) => (a.nombre || '').localeCompare(b.nombre || '')));
      setClienteId(nuevo.id);
      setNuevoCli(false); setCliNombre(''); setCliTel('');
      mostrarToast('Cliente creado ✅', 'exito');
    } catch (e) {
      mostrarToast(mensajeAmigable(e), 'error');
    } finally {
      setGuardandoCli(false);
    }
  };

  const guardar = async () => {
    if (!items || items.length === 0) { mostrarToast('La venta debe tener al menos un producto (si no, elimínala)', 'error'); return; }
    const sinPeso = items.find((i) => esPorPeso(i.unidad) && !(i.cantidad > 0));
    if (sinPeso) { mostrarToast(`Escribe cuántas libras de ${sinPeso.nombre} se vendieron`, 'error'); return; }
    if (aCredito && !clienteId) { mostrarToast('Para dejarla a crédito, elige o crea un cliente (para saber quién debe)', 'error', 5000); return; }
    setGuardando(true);
    try {
      await editarVenta(referencia, {
        items,
        clienteId: clienteId || null,
        metodoPago: aCredito ? null : (cuentas.find((c) => c.id === cuentaId)?.nombre || null),
        cuentaId: aCredito ? null : (cuentaId || null),
        descuento: desc,
        concepto: concepto || null,
        nota: nota || null,
        canal: canal || null,
        aCredito,
        envio: envioNum,
        fecha: timestampParaFecha(fechaVenta),
      });
      mostrarToast(aCredito
        ? 'Venta actualizada a crédito. Queda en Por cobrar 🧾'
        : 'Venta actualizada. Stock y dinero ajustados ✅', 'exito');
      onGuardado && onGuardado();
      onCerrar();
    } catch (e) {
      const msg = (e?.message || '').includes('STOCK_INSUFICIENTE')
        ? 'No hay stock suficiente para las cantidades nuevas.'
        : mensajeAmigable(e);
      mostrarToast(msg, 'error');
    } finally {
      setGuardando(false);
    }
  };

  const input = { width: '100%', padding: '9px', border: '1px solid #D1D5DB', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box' };

  return (
    <div onClick={onCerrar} style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100, padding: '16px' }}>
      <div onClick={(e) => e.stopPropagation()} style={{ backgroundColor: 'white', borderRadius: '16px', padding: '20px', width: '460px', maxWidth: '100%', maxHeight: '90vh', overflowY: 'auto', boxSizing: 'border-box' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
          <h3 style={{ margin: 0, color: 'var(--color-primario,#DB2777)' }}>✏️ Editar venta</h3>
          <button onClick={onCerrar} disabled={guardando} style={{ background: 'none', border: 'none', fontSize: '22px', cursor: 'pointer' }}>✕</button>
        </div>

        {error && <p style={{ color: '#991B1B', fontWeight: 600 }}>⚠️ {error}</p>}
        {!items && !error && <p style={{ color: '#6B7280', textAlign: 'center', padding: '20px' }}>⏳ Cargando venta...</p>}

        {items && (
          <>
            {items.length === 0 && (
              <p style={{ fontSize: '13px', color: '#DC2626', backgroundColor: '#FEE2E2', padding: '10px', borderRadius: '8px' }}>
                Quitaste todos los productos. Para dejar la venta vacía, mejor elimínala.
              </p>
            )}
            {items.map((i) => (
              <div key={i.producto_id} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 0', borderBottom: '1px solid #F3F4F6' }}>
                {/* Miniatura: para confirmar de un vistazo qué se vendió */}
                <div style={{ width: '44px', height: '44px', flexShrink: 0, borderRadius: '8px', overflow: 'hidden', backgroundColor: '#F9FAFB', border: '1px solid #F3F4F6', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '20px' }}>
                  {i.foto
                    ? <img src={optimizarFoto(i.foto, 120)} alt={i.nombre} loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    : '📦'}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ margin: 0, fontSize: '13px', fontWeight: 600 }}>{i.nombre}</p>
                  <p style={{ margin: 0, fontSize: '12px', color: '#6B7280' }}>{i.codigo ? `${i.codigo} · ` : ''}{fmt(i.precio)} {esPorPeso(i.unidad) ? `por ${i.unidad === 'kg' ? 'kilo' : 'libra'}` : 'c/u'} · máx {formatoCantidad(i.stockMax, i.unidad)}</p>
                </div>
                <button onClick={() => cambiar(i.producto_id, -1)} disabled={guardando} style={{ width: '26px', height: '26px', borderRadius: '6px', border: '1px solid #D1D5DB', background: 'white', cursor: 'pointer', fontWeight: 'bold' }}>−</button>
                {esPorPeso(i.unidad) ? (
                  <CampoNumero valor={i.cantTexto ?? String(i.cantidad)} onChange={(v) => escribirCantidad(i.producto_id, v)}
                    decimales={2} min={0.01} max={i.stockMax} sufijo={i.unidad} deshabilitado={guardando}
                    maxMensaje={`Máximo ${formatoCantidad(i.stockMax, i.unidad)}`} aria-label={`Libras de ${i.nombre}`}
                    estilo={{ width: '70px', padding: '4px 6px', borderRadius: '6px', fontSize: '14px', textAlign: 'center', fontWeight: 600 }} />
                ) : (
                  <span style={{ minWidth: '20px', textAlign: 'center', fontWeight: 600 }}>{i.cantidad}</span>
                )}
                <button onClick={() => cambiar(i.producto_id, 1)} disabled={guardando} style={{ width: '26px', height: '26px', borderRadius: '6px', border: '1px solid #D1D5DB', background: 'white', cursor: 'pointer', fontWeight: 'bold' }}>+</button>
                <span style={{ minWidth: '66px', textAlign: 'right', fontWeight: 'bold', color: 'var(--color-primario,#DB2777)', fontSize: '13px' }}>{fmt(i.precio * i.cantidad)}</span>
                <button onClick={() => quitar(i.producto_id)} disabled={guardando} title="Quitar" style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#DC2626', fontSize: '15px' }}>✕</button>
              </div>
            ))}

            {/* Tipo de pago: pagada o a crédito (fiado) */}
            <div style={{ display: 'flex', gap: '8px', margin: '12px 0 10px' }}>
              <button type="button" disabled={guardando} onClick={() => setAcredito(false)}
                style={{ flex: 1, padding: '8px', borderRadius: '8px', fontSize: '13px', fontWeight: 700, cursor: 'pointer',
                  border: `1px solid ${!aCredito ? 'var(--k-verde)' : '#E5E7EB'}`, backgroundColor: !aCredito ? 'var(--k-verde)' : 'white', color: !aCredito ? 'white' : '#6B7280' }}>
                💵 Pagada
              </button>
              <button type="button" disabled={guardando} onClick={() => setAcredito(true)}
                style={{ flex: 1, padding: '8px', borderRadius: '8px', fontSize: '13px', fontWeight: 700, cursor: 'pointer',
                  border: `1px solid ${aCredito ? '#B45309' : '#E5E7EB'}`, backgroundColor: aCredito ? '#F59E0B' : 'white', color: aCredito ? 'white' : '#6B7280' }}>
                🧾 A crédito (fiado)
              </button>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: aCredito ? '1fr' : '1fr 1fr', gap: '8px', marginBottom: '10px' }}>
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <label style={{ fontSize: '12px', color: '#6B7280', fontWeight: 600 }}>Cliente {aCredito ? '*' : ''}</label>
                  <button type="button" disabled={guardando} onClick={() => setNuevoCli((v) => !v)}
                    style={{ background: 'none', border: 'none', color: 'var(--color-primario,#DB2777)', fontSize: '12px', fontWeight: 700, cursor: 'pointer', padding: 0 }}>
                    {nuevoCli ? 'Cancelar' : '➕ Nuevo'}
                  </button>
                </div>
                <select value={clienteId} disabled={guardando} onChange={(e) => setClienteId(e.target.value)} style={input}>
                  <option value="">Sin cliente</option>
                  {clientes.map((c) => <option key={c.id} value={c.id}>{c.nombre}</option>)}
                </select>
              </div>
              {!aCredito && (
                <div>
                  <label style={{ fontSize: '12px', color: '#6B7280', fontWeight: 600 }}>Forma de pago / cuenta</label>
                  <select value={cuentaId} disabled={guardando} onChange={(e) => setCuentaId(e.target.value)} style={input}>
                    {cuentas.map((c) => <option key={c.id} value={c.id}>{iconoTipo(c.tipo)} {c.nombre}</option>)}
                  </select>
                </div>
              )}
            </div>

            {aCredito && (
              <p style={{ margin: '0 0 10px', fontSize: '12px', color: '#92400E', backgroundColor: '#FEF3C7', borderRadius: '8px', padding: '8px 10px' }}>
                🧾 El dinero queda <strong>pendiente en Por cobrar</strong>. Esta venta saldrá del Balance y los pagos (abonos) se registran en 🧾 Por cobrar.
              </p>
            )}

            {nuevoCli && (
              <div style={{ backgroundColor: '#FFF5F7', borderRadius: '8px', padding: '10px', border: '1px solid #FBCFE8', margin: '0 0 10px' }}>
                <input type="text" value={cliNombre} disabled={guardandoCli} autoFocus
                  onChange={(e) => setCliNombre(e.target.value)} placeholder="Nombre del cliente *"
                  style={{ ...input, marginBottom: '6px' }} />
                <input type="tel" value={cliTel} disabled={guardandoCli}
                  onChange={(e) => setCliTel(e.target.value)} placeholder="Teléfono (opcional)"
                  style={{ ...input, marginBottom: '8px' }} />
                <button type="button" onClick={guardarClienteRapido} disabled={guardandoCli}
                  style={{ width: '100%', padding: '9px', backgroundColor: guardandoCli ? '#9CA3AF' : 'var(--color-primario,#DB2777)', color: 'white', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: 'bold', cursor: guardandoCli ? 'wait' : 'pointer' }}>
                  {guardandoCli ? '⏳ Guardando...' : 'Guardar y usar'}
                </button>
              </div>
            )}
            <div style={{ marginBottom: '10px' }}>
              <label style={{ fontSize: '12px', color: '#6B7280', fontWeight: 600 }}>🗓️ Fecha de la venta</label>
              <input type="date" value={fechaVenta} max={fechaLocal()} disabled={guardando}
                onChange={(e) => setFechaVenta(e.target.value)} style={input} />
              <p style={{ margin: '3px 0 0', fontSize: '11px', color: '#9CA3AF' }}>
                {fechaVenta === fechaLocal()
                  ? `Hoy · ${horaLocal()} (hora actual)`
                  : '📅 Venta de otro día · se guarda con la hora actual'}
              </p>
            </div>
            <div style={{ marginBottom: '10px' }}>
              <label style={{ fontSize: '12px', color: '#6B7280', fontWeight: 600 }}>Descuento (L, opcional)</label>
              <CampoNumero valor={descuento} onChange={setDescuento} deshabilitado={guardando}
                min={0} max={subtotal} prefijo="L" placeholder="0.00"
                maxMensaje="El descuento no puede pasar del subtotal" estilo={input} />
            </div>
            <div style={{ marginBottom: '10px' }}>
              <label style={{ fontSize: '12px', color: '#6B7280', fontWeight: 600 }}>Concepto (opcional)</label>
              <input type="text" value={concepto} disabled={guardando}
                onChange={(e) => setConcepto(e.target.value)} style={input} placeholder="Ej: Venta a Gisela por WhatsApp" />
            </div>
            <div style={{ marginBottom: '10px' }}>
              <label style={{ fontSize: '12px', color: '#6B7280', fontWeight: 600 }}>Canal de venta</label>
              <select value={canal} disabled={guardando} onChange={(e) => setCanal(e.target.value)} style={input}>
                <option value="">Sin especificar</option>
                {CANALES.map((c) => <option key={c.valor} value={c.valor}>{c.icono} {c.valor}</option>)}
              </select>
            </div>
            <div style={{ marginBottom: '10px' }}>
              <label style={{ fontSize: '12px', color: '#6B7280', fontWeight: 600 }}>Nota (opcional)</label>
              <input type="text" value={nota} disabled={guardando}
                onChange={(e) => setNota(e.target.value)} style={input} placeholder="Ej: Entregar el viernes" />
            </div>

            <div style={{ marginBottom: '10px' }}>
              <label style={{ fontSize: '12px', color: '#6B7280', fontWeight: 600 }}>🚚 Envío nacional (opcional)</label>
              <CampoNumero valor={envio} onChange={setEnvio} deshabilitado={guardando}
                min={0} prefijo="L" placeholder="0.00" estilo={input} />
              <p style={{ margin: '3px 0 0', fontSize: '11px', color: '#9CA3AF' }}>
                {aCredito
                  ? 'Se suma a lo que la clienta debe (Por cobrar).'
                  : 'Se suma al total y entra a tu cuenta. Recordá registrar el gasto del courier ("Envíos").'}
              </p>
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', color: '#6B7280' }}>
              <span>Subtotal</span><span>{fmt(subtotal)}</span>
            </div>
            {desc > 0 && (
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', color: '#DC2626' }}>
                <span>Descuento</span><span>−{fmt(desc)}</span>
              </div>
            )}
            {envioNum > 0 && (
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', color: '#0EA5E9' }}>
                <span>🚚 Envío nacional</span><span>+{fmt(envioNum)}</span>
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '19px', fontWeight: 'bold', margin: '6px 0 14px' }}>
              <span>{envioNum > 0 ? 'Total a cobrar' : 'Total'}</span><span style={{ color: 'var(--color-primario,#DB2777)' }}>{fmt(total)}</span>
            </div>

            {!aCredito && esEfectivo && total > 0 && (
              <div style={{ backgroundColor: '#F0FDF4', borderRadius: '8px', padding: '10px 12px', border: '1px solid #BBF7D0', marginBottom: '12px' }}>
                <label style={{ fontSize: '12px', color: '#065F46', fontWeight: 600, display: 'block', marginBottom: '4px' }}>💵 ¿Con cuánto paga? (para el vuelto, opcional)</label>
                <CampoNumero valor={pagaCon} onChange={setPagaCon} deshabilitado={guardando}
                  min={0} prefijo="L" placeholder="0.00" estilo={input} />
                {pagaConNum > 0 && (
                  <p style={{ margin: '8px 0 0', fontSize: '15px', fontWeight: 'bold', color: vuelto >= 0 ? '#065F46' : '#DC2626' }}>
                    {vuelto >= 0 ? `Vuelto: ${fmt(vuelto)}` : `Falta: ${fmt(-vuelto)}`}
                  </p>
                )}
              </div>
            )}

            <button onClick={guardar} disabled={guardando || items.length === 0}
              style={{ width: '100%', padding: '13px', backgroundColor: (guardando || items.length === 0) ? '#9CA3AF' : (aCredito ? '#F59E0B' : 'var(--k-verde)'), color: 'white', border: 'none', borderRadius: '10px', fontSize: '15px', fontWeight: 'bold', cursor: (guardando || items.length === 0) ? 'not-allowed' : 'pointer' }}>
              {guardando ? '⏳ Guardando...' : (aCredito ? '🧾 Guardar a crédito' : '💾 Guardar cambios')}
            </button>
            <p style={{ margin: '10px 0 0', fontSize: '11px', color: '#9CA3AF', textAlign: 'center' }}>
              Los precios se toman del producto actual. Al guardar se ajustan el stock y el dinero automáticamente.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
